import { cn } from '@/lib/utils';
import { Icon as SharedIcon } from '@chiroup/components/Icon';
import { Icons } from '@chiroup/components/icons';
import { formatPhone } from '@chiroup/core/functions/format';
import * as Sentry from '@sentry/react';
import {
  CheckCircle as CheckCircleIcon,
  TriangleAlert as ExclamationTriangleIcon,
} from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { hcVerbiage } from '../../common/verbiage';
import { usePatientPortal } from '../../contexts/PatientPortalContext';
import healthcomService from '../../services/healthcom.service';
import DejavooCardEntry, { DejavooCardEntryRef } from '../DejavooCardEntry';
import UsaepayCardEntry, { UsaepayCardEntryRef } from '../UsaepayCardEntry';
import Modal from '../common/Modal';

const formatCurrency = (amount: number | undefined) => {
  const value = amount || 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
};

// The payment confirmation must survive remounts — portal-level
// refreshes after a payment can remount this page (skeleton swap,
// patient re-selection, even a full reload), and losing local state
// there means the patient never sees that their card was charged.
// Keyed by patient so household members don't see each other's
// confirmations; short TTL so it doesn't reappear on a later visit.
// Keyed by patient — and, under split-balance-by-location, also by the location
// paid — so the confirmation only shows on the location that was actually paid;
// other locations keep showing their own pay panel.
const paidStorageKey = (patientId: string, locationId?: number) =>
  `hc-balance-paid-${patientId}${locationId != null ? `-${locationId}` : ''}`;
const PAID_CONFIRMATION_TTL_MS = 60_000;

const readPaidConfirmation = (
  patientId?: string,
  locationId?: number
): { amount: number } | null => {
  if (!patientId) return null;
  try {
    const raw = sessionStorage.getItem(paidStorageKey(patientId, locationId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Number.isFinite(parsed?.amount) || !Number.isFinite(parsed?.at)) return null;
    if (Date.now() - parsed.at > PAID_CONFIRMATION_TTL_MS) return null;
    return { amount: parsed.amount };
  } catch {
    return null;
  }
};

const writePaidConfirmation = (patientId: string, amount: number, locationId?: number) => {
  try {
    sessionStorage.setItem(
      paidStorageKey(patientId, locationId),
      JSON.stringify({ amount, at: Date.now() })
    );
  } catch {
    // Storage unavailable/full — local state still shows the card.
  }
};

type SavedCard = {
  key: string;
  processor: 'usaepay' | 'dejavoo';
  cardholder?: string;
  cardType?: string;
  lastFour?: string;
  isDefault?: boolean;
  // When true, the processing fee is waived on transactions paid with this
  // card (no toggle is presented to the patient).
  suppressProcessingFee?: boolean;
  chargeable: boolean;
  // Location whose merchant/TPN this card is vaulted on. Present only under
  // split-balance-by-location; used to scope the picker per location.
  locationId?: number;
};

const BalancePage: React.FC = () => {
  const { selectedPatient, language, refreshPatientData } = usePatientPortal();
  const balances = selectedPatient?.balances;
  const primaryColor = selectedPatient?.clinic?.primaryColor || '#77bd43';

  const totalBalance = balances?.balance || 0;
  const patientBalance = balances?.patientBalance || 0;
  const payorBalance = balances?.payorBalance || 0;
  const creditBalance = balances?.creditBalance || 0;

  // Split-balance-by-location: the clinic has manual card devices across more
  // than one location, so balances are shown and paid per location. We only
  // treat it as active when the backend also returned a per-location breakdown.
  const byLocation = balances?.byLocation ?? [];
  const splitActive = !!selectedPatient?.splitBalanceByLocation && byLocation.length > 0;
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);

  // Default the selected location once the breakdown loads: the first location
  // that actually owes something, else the first. Keep the patient's choice if
  // it's still valid.
  useEffect(() => {
    if (!splitActive) {
      setSelectedLocationId(null);

      return;
    }

    setSelectedLocationId(prev => {
      if (prev != null && byLocation.some(l => l.locationId === prev)) return prev;
      const withBalance = byLocation.find(l => l.patientBalance > 0);

      return (withBalance || byLocation[0])?.locationId ?? null;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [splitActive, balances?.byLocation]);

  const activeLocation = splitActive
    ? (byLocation.find(l => l.locationId === selectedLocationId) ?? null)
    : null;

  // The balance the Pay panel operates on: the selected location's patient
  // balance under split, otherwise the clinic-wide patient balance. The rest of
  // the pay flow (amount prefill, validation, gating) keys off this so the
  // non-split path is unchanged (payableBalance === patientBalance).
  const payableBalance = splitActive ? (activeLocation?.patientBalance ?? 0) : patientBalance;

  // The location key the paid-confirmation and optimistic balance are scoped
  // to. Under split each location tracks its own; otherwise it's patient-wide.
  const paidLocationKey = splitActive ? (selectedLocationId ?? undefined) : undefined;

  // Optimistic post-payment balance. The server recompute on refresh can
  // momentarily lag the write (read-replica), so right after a successful
  // charge we floor the displayed balance to what we expect remains. We never
  // show MORE than the freshly-fetched value (Math.min below), so once the
  // refresh catches up this is a no-op — it just avoids briefly showing the
  // pre-payment balance. Keyed by the location that was paid; in-memory only.
  const [optimisticPaid, setOptimisticPaid] = useState<{
    locationKey?: number;
    remaining: number;
  } | null>(null);

  // Drop any stale optimistic value when the patient changes.
  useEffect(() => {
    setOptimisticPaid(null);
  }, [selectedPatient?.ID]);

  // Clear the optimistic floor once the server recompute has caught up (fresh
  // balance ≤ what we expected), so a later balance change in the same session
  // can't be masked by a stale optimistic value.
  useEffect(() => {
    if (!optimisticPaid) return;
    const fresh = splitActive
      ? (byLocation.find(l => l.locationId === optimisticPaid.locationKey)?.patientBalance ?? 0)
      : patientBalance;

    if (fresh <= optimisticPaid.remaining) setOptimisticPaid(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [balances, optimisticPaid, splitActive]);

  const optimisticRemainingFor = (locationKey?: number): number | null =>
    optimisticPaid && optimisticPaid.locationKey === locationKey ? optimisticPaid.remaining : null;

  // Balance shown for a location, reconciling the optimistic floor with fresh data.
  const displayedBalanceFor = (locationKey: number | undefined, freshBalance: number): number => {
    const optimistic = optimisticRemainingFor(locationKey);

    return optimistic != null ? Math.min(freshBalance, optimistic) : freshBalance;
  };

  const displayedPayable = displayedBalanceFor(paidLocationKey, payableBalance);

  const hasAnyBalance =
    totalBalance !== 0 || patientBalance !== 0 || payorBalance !== 0 || creditBalance !== 0;

  // Clinic-level "Allow patients to pay online through HealthCom"
  // toggle. Defaults to false (not allowed) when the field is missing —
  // a clinic must explicitly opt in to online payments.
  // When false: the Pay panel and Add-card affordance are hidden
  // and the patient is shown a note pointing them at the clinic.
  // The backend re-checks this on every charge — frontend gating
  // is just for UX.
  const onlinePaymentsAllowed = selectedPatient?.clinic?.allowOnlinePayments === true;

  // Cards fetched on mount via /me/{patientId}/payment-methods.
  // Empty until the call resolves.
  const [cards, setCards] = useState<SavedCard[]>([]);
  const [cardsLoading, setCardsLoading] = useState(false);
  const [cardsError, setCardsError] = useState<string | null>(null);
  const [selectedCardKey, setSelectedCardKey] = useState<string | null>(null);
  // Add-card config. Populated alongside the saved-cards list so we
  // know whether to show the "Add a new card" affordance and which
  // widget to render — Dejavoo (FTD SDK) or USAePay (hosted PayJS).
  const [addCardConfig, setAddCardConfig] = useState<
    | { processor: 'dejavoo'; ecommToken: string }
    | { processor: 'usaepay'; epayPublicKey: string }
    | null
  >(null);
  // Per-location add-card configs (split only), keyed by locationId, so a card
  // is tokenized against the merchant of the location it's being added for.
  const [addCardByLocation, setAddCardByLocation] = useState<Record<
    number,
    | { processor: 'dejavoo'; ecommToken: string }
    | { processor: 'usaepay'; epayPublicKey: string }
    | null
  > | null>(null);

  // The add-card config to actually use: under split, the SELECTED location's
  // config (so tokenize + save land on that location's merchant); otherwise the
  // clinic/home config. Null when the selected location has no manual merchant.
  const activeAddCardConfig = splitActive
    ? selectedLocationId != null
      ? (addCardByLocation?.[selectedLocationId] ?? null)
      : null
    : addCardConfig;
  // Clinic-level processing fee rate as a decimal (0.029 = 2.9%). 0 when
  // the clinic doesn't charge one. Returned by `listPaymentMethods`.
  const [processingFeeRate, setProcessingFeeRate] = useState<number>(0);
  const [addCardOpen, setAddCardOpen] = useState(false);
  const [addCardSubmitting, setAddCardSubmitting] = useState(false);
  const [addCardError, setAddCardError] = useState<string | null>(null);
  // Patient must explicitly authorize storing the card before we let
  // them submit. Reset whenever the modal opens or closes.
  const [addCardAuthorized, setAddCardAuthorized] = useState(false);
  const dejavooEntryRef = useRef<DejavooCardEntryRef | null>(null);
  const usaepayEntryRef = useRef<UsaepayCardEntryRef | null>(null);

  // Editable amount, prefilled to the patient responsibility. Stored
  // as a string while the user is typing so they can clear it; we
  // parse it at submit time.
  const [amountInput, setAmountInput] = useState<string>('');
  useEffect(() => {
    setAmountInput(payableBalance > 0 ? payableBalance.toFixed(2) : '');
  }, [payableBalance]);

  // Fetch saved cards once we know the patient. Hidden errors are
  // soft — the patient can still try to pay, and the backend will
  // surface a useful message if no card is actually charged-able.
  useEffect(() => {
    if (!selectedPatient?.ID) return;
    let cancelled = false;

    const run = async () => {
      setCardsLoading(true);
      setCardsError(null);
      try {
        const res = await healthcomService.listPaymentMethods(selectedPatient.ID);
        if (cancelled) return;
        const list = res?.data || [];
        setCards(list);
        setAddCardConfig(res?.addCard || null);
        setAddCardByLocation(res?.addCardByLocation || null);
        setProcessingFeeRate(Number(res?.processingFeeRate) || 0);
        // Prefer a chargeable default card → any chargeable card →
        // the first card on file (so non-chargeable cards still
        // display selected when they're all that's saved).
        const initial =
          list.find(c => c.isDefault && c.chargeable) || list.find(c => c.chargeable) || list[0];
        setSelectedCardKey(initial?.key ?? null);
      } catch (err: any) {
        if (cancelled) return;
        Sentry.captureException(err);
        setCardsError(err?.response?.data?.message || err?.message || 'Could not load cards.');
      } finally {
        if (!cancelled) setCardsLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [selectedPatient?.ID]);

  // Pay-down state. `paid` hydrates from sessionStorage so the
  // confirmation survives the page remounting right after payment, and
  // re-reads when the selected location changes so switching locations shows
  // that location's own paid/unpaid state (not a sibling's confirmation).
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);
  const [paid, setPaid] = useState<{ amount: number } | null>(() =>
    readPaidConfirmation(selectedPatient?.ID, paidLocationKey)
  );
  useEffect(() => {
    if (!selectedPatient?.ID) return;
    setPaid(readPaidConfirmation(selectedPatient.ID, paidLocationKey));
  }, [selectedPatient?.ID, paidLocationKey]);

  // Under split, only cards vaulted on the selected location's merchant can be
  // used — a card is merchant-scoped, so cross-location cards can't charge here.
  const visibleCards = splitActive ? cards.filter(c => c.locationId === selectedLocationId) : cards;

  // Keep the selected card valid for the chosen location: when the location
  // changes (or cards load), fall back to a chargeable card in that location.
  useEffect(() => {
    if (!splitActive) return;

    setSelectedCardKey(prev => {
      if (prev && visibleCards.some(c => c.key === prev)) return prev;
      const initial =
        visibleCards.find(c => c.isDefault && c.chargeable) ||
        visibleCards.find(c => c.chargeable) ||
        visibleCards[0];

      return initial?.key ?? null;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [splitActive, selectedLocationId, cards]);

  const parsedAmount = Number.parseFloat(amountInput);
  const amountValid = Number.isFinite(parsedAmount) && parsedAmount > 0;
  const amountTooHigh = amountValid && parsedAmount > payableBalance + 0.005;
  const selectedCard = cards.find(c => c.key === selectedCardKey) || null;
  const selectedChargeable = !!selectedCard?.chargeable;
  // Patient-side fee preview. The backend re-computes/charges authoritatively
  // and returns the final `amount` charged; this just shows the patient what
  // will hit their card. HealthCom rule: when the selected card is suppressed,
  // the fee is always off — no toggle is presented.
  const feeApplies = amountValid && processingFeeRate > 0 && !selectedCard?.suppressProcessingFee;
  const previewProcessingFee = feeApplies
    ? Math.round(parsedAmount * processingFeeRate * 100) / 100
    : 0;
  const previewTotal = (amountValid ? parsedAmount : 0) + previewProcessingFee;
  const canPay =
    !!selectedPatient?.ID &&
    payableBalance > 0 &&
    amountValid &&
    !amountTooHigh &&
    !!selectedCardKey &&
    selectedChargeable &&
    // Under split the patient must have a location selected to pay.
    (!splitActive || selectedLocationId != null);

  const reloadCards = async (selectKey?: string) => {
    if (!selectedPatient?.ID) return;
    try {
      const res = await healthcomService.listPaymentMethods(selectedPatient.ID);
      const list = res?.data || [];
      setCards(list);
      setAddCardConfig(res?.addCard || null);
      setProcessingFeeRate(Number(res?.processingFeeRate) || 0);
      if (selectKey && list.some(c => c.key === selectKey)) {
        setSelectedCardKey(selectKey);
      }
    } catch (err) {
      Sentry.captureException(err);
    }
  };

  const submitNewCard = async () => {
    if (!selectedPatient?.ID || !activeAddCardConfig) return;

    // Save the card against the location being paid (split) so it lands on the
    // same merchant it was tokenized against; backend falls back to home when
    // omitted.
    const addCardLocationId = splitActive ? (selectedLocationId ?? undefined) : undefined;

    setAddCardSubmitting(true);
    setAddCardError(null);

    try {
      // Both processors tokenize client-side — the PAN never crosses our
      // wire, only the resulting token (+ last4/brand when the SDK
      // exposes them).
      let newKey: string | undefined;

      if (activeAddCardConfig.processor === 'usaepay') {
        if (!usaepayEntryRef.current) return;

        const result = await usaepayEntryRef.current.tokenize();

        if (!result) {
          // UsaepayCardEntry already surfaced an error via onError.
          return;
        }

        const res = await healthcomService.addPaymentMethod({
          patientId: selectedPatient.ID,
          paymentKey: result.paymentKey,
          cardholderName:
            `${selectedPatient.fname || ''} ${selectedPatient.lname || ''}`.trim() || undefined,
          locationId: addCardLocationId,
        });

        newKey = res?.data?.key;
      } else {
        if (!dejavooEntryRef.current) return;

        const result = await dejavooEntryRef.current.tokenize();

        if (!result) {
          // DejavooCardEntry already surfaced an error via onError.
          return;
        }

        const cardholderName =
          `${result.cardData.cardholderFirstName} ${result.cardData.cardholderLastName}`.trim();

        const res = await healthcomService.addPaymentMethod({
          patientId: selectedPatient.ID,
          paymentTokenId: result.paymentTokenId,
          cardholderName: cardholderName || undefined,
          postalCode: result.cardData.postalCode || undefined,
          cardLastFour: result.cardData.cardLastFour,
          cardType: result.cardData.cardType,
          locationId: addCardLocationId,
        });

        newKey = res?.data?.key;
      }

      await reloadCards(newKey);
      setAddCardOpen(false);
    } catch (err: any) {
      Sentry.captureException(err);
      setAddCardError(
        err?.response?.data?.message ||
          err?.message ||
          (language === 'es'
            ? 'No pudimos guardar la tarjeta. Intente de nuevo.'
            : 'We couldn’t save that card. Please try again.')
      );
    } finally {
      setAddCardSubmitting(false);
    }
  };

  // Return to the pay panel after a payment. The confirmation replaces the
  // panel (and survives refreshes via sessionStorage), so without this the
  // patient has no way to pay a remaining balance until the TTL lapses.
  const dismissPaidConfirmation = () => {
    setPaid(null);
    if (selectedPatient?.ID) {
      try {
        sessionStorage.removeItem(paidStorageKey(selectedPatient.ID, paidLocationKey));
      } catch {
        // Storage unavailable — clearing local state is enough.
      }
    }
    // Re-prefill the amount; it still holds the amount just paid otherwise.
    setAmountInput(payableBalance > 0 ? payableBalance.toFixed(2) : '');
  };

  const submitPayment = async () => {
    if (!selectedPatient?.ID || !canPay) return;

    setPaying(true);
    setPayError(null);

    try {
      await healthcomService.payBalance({
        patientId: selectedPatient.ID,
        amount: parsedAmount,
        payMethodKey: selectedCardKey || undefined,
        locationId: splitActive ? (selectedLocationId ?? undefined) : undefined,
      });
      // The charge succeeded — show the confirmation immediately. The
      // balance refresh below must not be able to mask a successful
      // payment as a failure. Per spec, the confirmation reflects the
      // exact amount the patient submitted (parsedAmount), not any
      // backend-adjusted figure.
      setPaid({ amount: parsedAmount });
      writePaidConfirmation(selectedPatient.ID, parsedAmount, paidLocationKey);
      // Reflect the reduced balance immediately, in case the refresh recompute
      // momentarily lags the write. `payableBalance` here is the pre-payment
      // balance for the location just paid.
      setOptimisticPaid({
        locationKey: paidLocationKey,
        remaining: Math.max(0, payableBalance - parsedAmount),
      });
      await refreshPatientData().catch(err => Sentry.captureException(err));
    } catch (err: any) {
      // Log the real failure for debugging, but show the patient the
      // fixed message the spec requires, regardless of the cause.
      Sentry.captureException(err);
      setPayError(
        language === 'es'
          ? 'Pago fallido. Comuníquese con el consultorio para pagar su saldo.'
          : 'Payment unsuccessful. Please contact the clinic to pay your balance.'
      );
    } finally {
      setPaying(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          {hcVerbiage.balance[language]}
        </h2>
        <p className="mt-1 text-sm text-gray-400 dark:text-gray-500 dark:text-gray-400">
          {hcVerbiage.balanceDescription[language]}
        </p>
      </div>

      {/* Per-location selector — only when the clinic splits balances by
          location. Each location settles to its own merchant/TPN, so the
          patient picks which location's balance to pay; the card below and the
          Pay panel scope to the selection. */}
      {onlinePaymentsAllowed && splitActive && (
        <div className="flex flex-col gap-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            {language === 'es' ? 'Ubicación' : 'Location'}
          </span>
          <div className="flex flex-col gap-2">
            {byLocation.map(loc => {
              const selected = loc.locationId === selectedLocationId;
              const shownBalance = displayedBalanceFor(loc.locationId, loc.patientBalance);

              return (
                <button
                  type="button"
                  key={loc.locationId}
                  onClick={() => setSelectedLocationId(loc.locationId)}
                  className="flex items-center justify-between rounded-xl px-4 py-3 text-left ring-1 transition bg-white dark:bg-white/5 disabled:opacity-50"
                  style={{
                    ['--ring' as any]: primaryColor,
                    boxShadow: selected ? `0 0 0 2px ${primaryColor}` : undefined,
                  }}
                >
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {loc.locationName ||
                      (language === 'es'
                        ? `Ubicación ${loc.locationId}`
                        : `Location ${loc.locationId}`)}
                  </span>
                  <span className="text-sm font-semibold tabular-nums text-gray-900 dark:text-gray-100">
                    {formatCurrency(shownBalance)}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Total Balance Card — hidden entirely when the clinic has
          online payments turned off; those patients only see the
          "online payments unavailable" note below. Under split it reflects the
          selected location's balance. */}
      {onlinePaymentsAllowed && (
        <div
          className="rounded-3xl p-6 text-white dark:brightness-90 dark:ring-1 dark:ring-white/10"
          style={{ backgroundColor: primaryColor }}
        >
          <p className="text-sm font-medium opacity-80">
            {splitActive
              ? activeLocation?.locationName ||
                (language === 'es' ? 'Saldo de la ubicación' : 'Location Balance')
              : language === 'es'
                ? 'Saldo Total'
                : 'Total Balance'}
          </p>
          <p className="mt-2 text-3xl font-bold">{formatCurrency(displayedPayable)}</p>
          <p className="mt-1 text-xs opacity-60">
            {selectedPatient?.fname} {selectedPatient?.lname}
          </p>
        </div>
      )}

      {/* Balance Breakdown — currently the only row we surface to
          patients is "Patient Responsibility", and in practice it
          always equals the Total Balance card above (the breakdown
          collapses to a single value once payor/credit/unallocated
          are excluded). Hidden for now to avoid showing the same
          number twice. Restore when there's more than one row to
          show. */}
      {/* {hasAnyBalance && (
        <div className="portal-card divide-y divide-gray-100 dark:divide-white/5">
          <BalanceRow
            icon={UserIcon}
            primaryColor={primaryColor}
            label={language === 'es' ? 'Responsabilidad del Paciente' : 'Patient Responsibility'}
            description={language === 'es' ? 'Monto que usted debe' : 'Amount you owe'}
            amount={patientBalance}
          />
        </div>
      )} */}

      {/* Online payments are disabled clinic-wide — show an
          informational note in place of the Pay panel. The Total
          Balance card and contact line are hidden in this state. */}
      {patientBalance > 0 && !paid && !onlinePaymentsAllowed && (
        <div className="portal-card p-5 sm:p-6">
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            {language === 'es' ? 'Pagos en línea no disponibles' : 'Online payments unavailable'}
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {language === 'es'
              ? 'Esta clínica no acepta pagos a través de HealthCom en este momento.'
              : 'This clinic isn’t accepting payments through HealthCom right now.'}
            {selectedPatient?.clinic?.phone ? (
              <>
                {language === 'es' ? ' Llame al ' : ' Call '}
                <a
                  href={`tel:${selectedPatient.clinic.phone}`}
                  className="font-semibold"
                  style={{ color: primaryColor }}
                >
                  {formatPhone(selectedPatient.clinic.phone, true)}
                </a>
                {language === 'es' ? ' para pagar su saldo.' : ' to pay your balance.'}
              </>
            ) : language === 'es' ? (
              ' Contacte al consultorio para pagar su saldo.'
            ) : (
              ' Contact the clinic to pay your balance.'
            )}
          </p>
        </div>
      )}

      {/* Pay panel — only when the patient has actual responsibility.
          Under split this is the selected location's balance. Insurance /
          credit-only states get nothing here. */}
      {payableBalance > 0 && !paid && onlinePaymentsAllowed && (
        <div className="portal-card p-5 sm:p-6 flex flex-col gap-5">
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              {language === 'es' ? 'Pagar mi saldo' : 'Pay your balance'}
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {language === 'es'
                ? 'Aplicaremos el pago a sus facturas comenzando por la más antigua.'
                : 'We’ll apply your payment to your invoices starting with the oldest first.'}
            </p>
          </div>

          {/* Amount input */}
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              {language === 'es' ? 'Monto' : 'Amount'}
            </span>
            <span className="relative">
              <span
                aria-hidden="true"
                className="absolute left-3 top-1/2 -translate-y-1/2 text-base font-semibold text-gray-500 dark:text-gray-400"
              >
                $
              </span>
              <input
                type="text"
                inputMode="decimal"
                value={amountInput}
                onChange={e => setAmountInput(e.target.value.replace(/[^0-9.]/g, ''))}
                disabled={paying}
                className="w-full pl-7 pr-3 py-2.5 text-lg font-semibold tabular-nums rounded-xl bg-white dark:bg-white/5 ring-1 ring-gray-950/10 dark:ring-white/15 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[--ring] disabled:opacity-50"
                style={{ ['--ring' as any]: primaryColor } as React.CSSProperties}
                placeholder={payableBalance.toFixed(2)}
              />
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {language === 'es'
                ? `Saldo del paciente: ${formatCurrency(payableBalance)}`
                : `Patient balance: ${formatCurrency(payableBalance)}`}
            </span>
            {amountTooHigh && (
              <span className="text-xs text-red-600 dark:text-red-400">
                {language === 'es'
                  ? `El monto no puede ser mayor a ${formatCurrency(payableBalance)}.`
                  : `Amount can’t be more than ${formatCurrency(payableBalance)}.`}
              </span>
            )}
          </label>

          {/* Card picker */}
          <div className="flex flex-col gap-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              {language === 'es' ? 'Método de pago' : 'Payment method'}
            </span>
            {cardsLoading ? (
              <div className="rounded-xl px-4 py-3 text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-white/5">
                {language === 'es' ? 'Cargando tarjetas…' : 'Loading cards…'}
              </div>
            ) : visibleCards.length === 0 ? (
              <div className="rounded-xl px-4 py-3 text-sm text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-white/5 ring-1 ring-gray-950/5 dark:ring-white/10">
                {splitActive
                  ? language === 'es'
                    ? 'No hay tarjetas guardadas para esta ubicación. Agregue una nueva tarjeta para pagar.'
                    : 'No saved cards for this location. Add a new card to pay.'
                  : language === 'es'
                    ? 'No hay tarjetas guardadas. Agregue una nueva tarjeta para pagar.'
                    : 'No saved cards. Add a new card to pay.'}
                {cardsError && (
                  <span className="block mt-1 text-xs text-red-600 dark:text-red-400">
                    {cardsError}
                  </span>
                )}
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {visibleCards.map(card => {
                  const checked = card.key === selectedCardKey;

                  return (
                    <label
                      key={card.key}
                      className={`flex items-center gap-3 cursor-pointer rounded-xl px-4 py-3 ring-1 transition-colors ${
                        checked
                          ? 'ring-2 bg-[--tint]'
                          : 'ring-gray-950/10 dark:ring-white/15 bg-white dark:bg-white/5 hover:bg-gray-50 dark:hover:bg-white/10'
                      }`}
                      style={
                        {
                          ['--ring' as any]: primaryColor,
                          ['--tint' as any]: `${primaryColor}10`,
                          ...(checked ? ({ ['--tw-ring-color' as any]: primaryColor } as any) : {}),
                          borderColor: checked ? primaryColor : undefined,
                        } as React.CSSProperties
                      }
                    >
                      <input
                        type="radio"
                        name="paymentMethod"
                        value={card.key}
                        checked={checked}
                        disabled={paying}
                        onChange={() => setSelectedCardKey(card.key)}
                        className="sr-only"
                      />
                      <span
                        className="size-9 rounded-xl flex items-center justify-center shrink-0"
                        style={{ backgroundColor: `${primaryColor}18`, color: primaryColor }}
                        aria-hidden="true"
                      >
                        <SharedIcon icon={Icons.creditCard} className="size-5" />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-semibold text-gray-900 dark:text-gray-100">
                          {card.cardType
                            ? `${card.cardType.toUpperCase()} •••• ${card.lastFour || '••••'}`
                            : card.lastFour
                              ? `Card •••• ${card.lastFour}`
                              : 'Saved card'}
                        </span>
                        {card.cardholder && (
                          <span className="block text-xs text-gray-500 dark:text-gray-400 truncate">
                            {card.cardholder}
                          </span>
                        )}
                        {!card.chargeable && (
                          <span className="block text-xs mt-0.5 text-amber-600 dark:text-amber-400">
                            {language === 'es'
                              ? 'Solo se puede usar en el consultorio'
                              : 'In-clinic use only'}
                          </span>
                        )}
                      </span>
                      {checked && (
                        <CheckCircleIcon
                          className="size-5 shrink-0"
                          style={{ color: primaryColor }}
                          aria-hidden="true"
                        />
                      )}
                    </label>
                  );
                })}
              </div>
            )}

            {/* Add-new-card affordance. Only rendered when the clinic supports
                portal-side card adds for the selected location (the listing
                endpoint returns config per location under split). */}
            {activeAddCardConfig && (
              <button
                type="button"
                onClick={() => {
                  setAddCardError(null);
                  setAddCardAuthorized(false);
                  setAddCardOpen(true);
                }}
                className="self-start mt-1 inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
              >
                <SharedIcon icon={Icons.add} className="size-4" />
                {language === 'es' ? 'Agregar nueva tarjeta' : 'Add a new card'}
              </button>
            )}
          </div>

          {selectedCard && !selectedCard.chargeable && (
            <p className="text-sm text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/10 rounded-xl px-3 py-2">
              {language === 'es'
                ? 'Esta tarjeta solo se puede usar en el consultorio. Contacte a la clínica para pagar por teléfono.'
                : 'This saved card can only be used at the clinic. Please contact the clinic to pay over the phone.'}
            </p>
          )}

          {feeApplies && (
            <div className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-white/5 rounded-xl px-3 py-2 flex flex-col gap-1">
              <div className="flex justify-between">
                <span>{language === 'es' ? 'Subtotal' : 'Subtotal'}</span>
                <span className="tabular-nums">{formatCurrency(parsedAmount)}</span>
              </div>
              <div className="flex justify-between">
                <span>
                  {language === 'es'
                    ? `Tarifa de procesamiento (${(processingFeeRate * 100).toFixed(1)}%)`
                    : `Processing fee (${(processingFeeRate * 100).toFixed(1)}%)`}
                </span>
                <span className="tabular-nums">{formatCurrency(previewProcessingFee)}</span>
              </div>
              <div className="flex justify-between font-semibold text-gray-900 dark:text-gray-100">
                <span>{language === 'es' ? 'Total' : 'Total'}</span>
                <span className="tabular-nums">{formatCurrency(previewTotal)}</span>
              </div>
            </div>
          )}

          {payError && (
            <p className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-500/10 rounded-xl px-3 py-2">
              {payError}
            </p>
          )}

          <button
            type="button"
            onClick={submitPayment}
            disabled={!canPay || paying}
            className="self-stretch sm:self-start inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98] transition-transform"
            style={{ backgroundColor: primaryColor }}
          >
            <SharedIcon icon={Icons.creditCard} className="size-4" />
            {paying
              ? language === 'es'
                ? 'Procesando…'
                : 'Processing…'
              : `${language === 'es' ? 'Pagar' : 'Pay'} ${formatCurrency(previewTotal)}`}
          </button>
        </div>
      )}

      {paid && (
        <div className="portal-card p-5 flex items-center gap-3">
          <span
            className="size-10 rounded-xl flex items-center justify-center shrink-0"
            style={{ backgroundColor: `${primaryColor}18`, color: primaryColor }}
            aria-hidden="true"
          >
            <CheckCircleIcon className="size-5" />
          </span>
          <div className="flex-1">
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {language === 'es'
                ? `¡Pago de ${formatCurrency(paid.amount)} procesado con éxito!`
                : `Payment of ${formatCurrency(paid.amount)} processed successfully!`}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {language === 'es' ? '¡Gracias!' : 'Thanks!'}
            </p>
            {/* Only when there's still a balance to pay — dismissing with a
                zero balance would reveal nothing. */}
            {payableBalance > 0 && (
              <button
                type="button"
                onClick={dismissPaidConfirmation}
                className="mt-3 inline-flex items-center justify-center gap-2 rounded-full px-4 py-2 text-xs font-semibold text-white active:scale-[0.98] transition-transform"
                style={{ backgroundColor: primaryColor }}
              >
                <SharedIcon icon={Icons.creditCard} className="size-4" />
                {language === 'es' ? 'Hacer otro pago' : 'Make another payment'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* No Balance State */}
      {!hasAnyBalance && (
        <div className="portal-card px-6 py-16 text-center">
          <SharedIcon
            icon={Icons.creditCard}
            className="mx-auto h-12 w-12 text-gray-300 dark:text-gray-600"
          />
          <h3 className="mt-4 text-base font-semibold text-gray-900 dark:text-gray-100">
            {language === 'es' ? 'Sin saldo pendiente' : 'No Outstanding Balance'}
          </h3>
          <p className="mt-2 text-sm text-gray-400 dark:text-gray-500 dark:text-gray-400 max-w-sm mx-auto">
            {language === 'es'
              ? 'Su cuenta está al día. No tiene saldos pendientes.'
              : "You're all caught up! There are no outstanding balances on your account."}
          </p>
        </div>
      )}

      {/* Contact Info — hidden when online payments are off; the
          "online payments unavailable" note already gives patients
          the clinic's number to call. */}
      {onlinePaymentsAllowed && hasAnyBalance && selectedPatient?.clinic?.phone && (
        <div className="portal-card p-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {language === 'es'
              ? 'Si tiene preguntas sobre su saldo, contáctenos al '
              : 'Questions about your balance? Contact us at '}
            <a
              href={`tel:${selectedPatient.clinic.phone}`}
              className="font-semibold"
              style={{ color: primaryColor }}
            >
              {formatPhone(selectedPatient.clinic.phone, true)}
            </a>
          </p>
        </div>
      )}

      {/* Add-card modal. Mounts the processor-appropriate card entry so
          the patient can save a new card from the portal — Dejavoo via
          the FTD SDK, USAePay via the hosted PayJS client. Tokenization
          happens client-side; only the resulting token (+ last4/brand
          when available) crosses our wire. */}
      <Modal
        isOpen={addCardOpen && !!activeAddCardConfig}
        close={() => {
          if (addCardSubmitting) return;
          setAddCardOpen(false);
          setAddCardError(null);
        }}
        size="default"
        showCloseButton
        disableClickOutsideClose={addCardSubmitting}
      >
        {/* No `overflow-y-auto` here — it forces `overflow-x: hidden`,
            which clips the input focus ring. Radix's DialogContent
            already constrains height + scrolls when content is taller
            than the viewport. */}
        <div className="flex flex-col gap-4">
          <div className="pr-8">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              {language === 'es' ? 'Agregar nueva tarjeta' : 'Add a new card'}
            </h3>
          </div>

          {activeAddCardConfig?.processor === 'dejavoo' && (
            <DejavooCardEntry
              ref={dejavooEntryRef}
              ecommToken={activeAddCardConfig.ecommToken}
              onTokenReceived={() => {
                /* `tokenize()` resolves with the same data; nothing else needed here */
              }}
              onError={msg => setAddCardError(msg)}
              isSubmitting={addCardSubmitting}
              defaultCardholderName={
                selectedPatient
                  ? `${selectedPatient.fname || ''} ${selectedPatient.lname || ''}`.trim()
                  : ''
              }
              showSaveCard={false}
            />
          )}

          {activeAddCardConfig?.processor === 'usaepay' && (
            <UsaepayCardEntry
              ref={usaepayEntryRef}
              epayPublicKey={activeAddCardConfig.epayPublicKey}
              onError={msg => setAddCardError(msg)}
            />
          )}

          {addCardError && (
            <p className="flex items-start gap-2 text-xs text-red-600 dark:text-red-400">
              <ExclamationTriangleIcon className="size-4 shrink-0 mt-px" aria-hidden="true" />
              <span>
                {/^DECLINE$/i.test(addCardError.trim())
                  ? language === 'es'
                    ? 'Tarjeta rechazada. Pruebe con otra tarjeta.'
                    : 'Card was declined — try a different one.'
                  : addCardError}
              </span>
            </p>
          )}

          {/* Patient must authorize storing the card before submitting.
              Clinic name populates from the assigned clinic. */}
          <label className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-300 cursor-pointer">
            <input
              type="checkbox"
              checked={addCardAuthorized}
              onChange={e => setAddCardAuthorized(e.target.checked)}
              disabled={addCardSubmitting}
              className="mt-0.5 size-4 shrink-0 rounded border-gray-300 dark:border-gray-600 disabled:opacity-50"
              style={{ accentColor: primaryColor }}
            />
            <span>
              {language === 'es'
                ? `Al guardar esta tarjeta, autorizo a ${selectedPatient?.clinic?.name || 'la clínica'} a almacenar mi información de pago para uso futuro.`
                : `By saving this card, I authorize ${selectedPatient?.clinic?.name || 'the clinic'} to store my payment information for future use.`}
            </span>
          </label>

          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => {
                if (addCardSubmitting) return;
                setAddCardOpen(false);
                setAddCardError(null);
              }}
              disabled={addCardSubmitting}
              className="text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 px-2.5 py-1.5 rounded-md disabled:opacity-50"
            >
              {language === 'es' ? 'Cancelar' : 'Cancel'}
            </button>
            <button
              type="button"
              onClick={submitNewCard}
              disabled={addCardSubmitting || !addCardAuthorized}
              className="inline-flex items-center justify-center rounded-full px-4 py-1.5 text-xs font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98] transition-transform"
              style={{ backgroundColor: primaryColor }}
            >
              {addCardSubmitting
                ? language === 'es'
                  ? 'Guardando…'
                  : 'Saving…'
                : language === 'es'
                  ? 'Guardar tarjeta'
                  : 'Save card'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

const BalanceRow: React.FC<{
  icon: React.ElementType;
  primaryColor: string;
  label: string;
  description: string;
  amount: number;
  isCredit?: boolean;
}> = ({ icon: Icon, primaryColor, label, description, amount, isCredit }) => (
  <div className="flex items-center px-4 py-4 gap-4">
    <span
      className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
      style={{ backgroundColor: `${primaryColor}15` }}
    >
      <Icon className="h-5 w-5" style={{ color: primaryColor }} />
    </span>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{label}</p>
      <p className="text-xs text-gray-400 dark:text-gray-500 dark:text-gray-400">{description}</p>
    </div>
    <p
      className={cn(
        'text-sm font-bold tabular-nums',
        isCredit
          ? 'text-green-600'
          : amount > 0
            ? 'text-gray-900 dark:text-gray-100'
            : 'text-gray-400 dark:text-gray-500 dark:text-gray-400'
      )}
    >
      {formatCurrency(Math.abs(amount))}
    </p>
  </div>
);

export default BalancePage;
